import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { brl, downloadCSV, rows } from "@/lib/api";
import { BarChart3, Download, Globe, Package, Plus, Receipt, ScanBarcode, UserCog, Wallet } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import useFetch from "@/hooks/useFetch";
import { EmptyState, ErrorState, KpiCard, KpiSkeleton, TableSkeleton } from "@/components/panel/ui";
import { Badge, Button, Card, CardHeader, PageHeader, Select } from "@/components/ui-kit";
import { usePanelLink } from "@/components/panel/screens";

/**
 * Classe ABC em tom semântico, no lugar do mapa de classes cruas.
 * A = os produtos que fazem 80% da receita (tom de dinheiro), B = a faixa
 * seguinte, que merece atenção, C = cauda longa, informação neutra.
 */
const CLASS_TONE = { A: "success", B: "warning", C: "neutral" };

/**
 * Painel de resumo do relatório: cabeçalho + lista embutida.
 *
 * De propósito NÃO usa DataTable — ele renderiza o próprio Card e aninharia uma
 * superfície dentro desta. Aqui o conteúdo é um resumo embutido (sem cabeçalho
 * de coluna, sem ações por linha), o mesmo caso das mini-listas do Dashboard.
 * O que o DataTable traria de fato — carregando, erro e vazio com saída — está
 * reproduzido abaixo.
 *
 * Os conteúdos são LISTAS, não tabelas: sem as colunas do DataTable não há como
 * virar cartão no celular, e uma tabela de 5 colunas em 356px só rolava para o
 * lado escondendo a última.
 */
function ReportPanel({ title, icon, loading, error, onRetry, isEmpty, empty, className, children }) {
  return (
    <Card className={className}>
      <CardHeader title={title} icon={icon} />
      {error ? <ErrorState onRetry={onRetry} />
        : loading ? <TableSkeleton rows={5} cols={4} />
        : isEmpty ? <EmptyState {...empty} />
        : children}
    </Card>
  );
}

export default function Reports() {
  // Link para o painel ATUAL: a mesma tela serve admin, vendedor e balcão.
  const link = usePanelLink();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);

  // useFetch no lugar de api.get().then(): os três blocos falham de forma
  // independente, e antes um erro em qualquer um deixava o painel vazio para
  // sempre, sem mensagem nem forma de tentar de novo.
  const { data: sales, error: salesError, retry: retrySales } = useFetch("/admin/reports/sales", { days });
  const { data: abcData, loading: abcLoading, error: abcError, retry: retryAbc } = useFetch("/admin/reports/abc");
  const { data: sellersData, loading: sellersLoading, error: sellersError, retry: retrySellers } = useFetch("/admin/sellers", { per_page: 100 });

  // rows(): /admin/sellers passou a responder no envelope { data, meta }.
  const abc = rows(abcData);
  const sellers = rows(sellersData);

  const exportCSV = () => downloadCSV(
    `curva-abc-${new Date().toISOString().slice(0, 10)}.csv`,
    ["Produto", "Qtd", "Receita", "Share %", "Classe"],
    abc.map((i) => [i.name, i.qty, i.revenue.toFixed(2).replace(".", ","), i.share, i.class]),
  );

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      <PageHeader
        eyebrow="Visão Geral"
        title="Relatórios"
        icon={BarChart3}
        description="Receita por canal, curva ABC e desempenho da equipe. O período vale só para os indicadores e o gráfico: a curva ABC olha todo o histórico e o quadro de vendedores, o mês corrente."
        actions={(
          // Antes o clique com a lista vazia não fazia nada e nem dizia por quê.
          <Button variant="secondary" icon={Download} onClick={exportCSV} disabled={!abc.length}
            data-testid="reports-export-btn" title={abc.length ? undefined : "Nada para exportar ainda"}>
            CSV
          </Button>
        )}
        filters={(
          // Filtro único: fica na linha mesmo no celular. Uma folha de filtros
          // (FilterSheet) só esconderia um select atrás de mais um toque.
          <Select value={days} onChange={(e) => setDays(e.target.value)} data-testid="reports-period-select"
            aria-label="Período do relatório" className="w-auto px-2.5 text-slate-600">
            <option value={7}>Últimos 7 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
          </Select>
        )}
      />

      {/* Sem `loading` na condição de propósito: ao trocar o período o useFetch
          mantém os números anteriores até chegarem os novos, e o esqueleto aqui
          faria os quatro indicadores piscarem a cada mudança de filtro. */}
      {salesError ? <ErrorState onRetry={retrySales} />
        : !sales ? <KpiSkeleton />
        : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={Globe} label="Online" testId="report-kpi-online"
              value={<>{brl(sales.total_online)} <span className="text-xs font-normal text-slate-400">({sales.count_online})</span></>} />
            <KpiCard icon={ScanBarcode} label="Balcão" testId="report-kpi-balcao"
              value={<>{brl(sales.total_balcao)} <span className="text-xs font-normal text-slate-400">({sales.count_balcao})</span></>} />
            <KpiCard icon={Receipt} label="Ticket médio" value={brl(sales.ticket_medio)} testId="report-kpi-ticket" />
            <KpiCard icon={Wallet} label="Total período" value={brl(sales.total_online + sales.total_balcao)} cls="text-brand-700" testId="report-kpi-total" />
          </div>
        )}

      {/* `!salesError` também: com o período em erro o bloco acima já oferece
          "tentar novamente", e manter aqui embaixo a curva do período anterior
          faria a mesma tela contar duas histórias ao mesmo tempo. */}
      {!salesError && sales && sales.series.length > 0 && (
        <Card>
          <CardHeader title="Vendas por canal (diário)" icon={BarChart3} />
          <div className="p-5">
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={sales.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => brl(v)} />
                  <Area type="monotone" dataKey="online" stroke="var(--brand)" fill="var(--brand)" fillOpacity={0.2} name="Online" />
                  <Area type="monotone" dataKey="balcao" stroke="var(--navy)" fill="var(--navy)" fillOpacity={0.2} name="Balcão" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ReportPanel
          title="Curva ABC de produtos" icon={Package}
          loading={abcLoading} error={abcError} onRetry={retryAbc}
          isEmpty={abc.length === 0}
          empty={{
            icon: Package,
            title: "Sem vendas ainda",
            desc: "A curva classifica os produtos pela participação na receita acumulada. Ela aparece depois da primeira venda.",
            action: <Button icon={ScanBarcode} onClick={() => navigate(link("pdv"))}>Abrir o PDV</Button>,
            testId: "abc-empty",
          }}
        >
          {/* Lista, não tabela. Em 390px as cinco colunas somavam 381px num
              espaço de 356px: o nome do produto quebrava em quatro linhas e o
              share ficava fora da borda, alcançável só por um scroll lateral
              que nada indicava existir. Empilhado, tudo cabe e nada some. */}
          <ul className="divide-y divide-slate-100">
            {abc.map((p, i) => (
              <li key={i} className="px-5 py-2.5 flex items-center gap-3" data-testid="abc-row">
                <Badge tone={CLASS_TONE[p.class]} className="shrink-0">{p.class}</Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-slate-900 truncate">{p.name}</p>
                  <p className="text-xs text-slate-400 font-mono tabular-nums">{p.qty} un. · {p.share}% da receita</p>
                </div>
                <span className="font-mono tabular-nums font-semibold text-slate-900 shrink-0">{brl(p.revenue)}</span>
              </li>
            ))}
          </ul>
        </ReportPanel>

        <ReportPanel
          className="h-fit"
          title="Vendas por vendedor (mês)" icon={UserCog}
          loading={sellersLoading} error={sellersError} onRetry={retrySellers}
          isEmpty={sellers.length === 0}
          empty={{
            icon: UserCog,
            // O endpoint lista só quem está ativo, então "cadastrado" seria
            // falso com a equipe toda desativada.
            title: "Nenhum vendedor ativo",
            desc: "A comissão do balcão é apurada por vendedor. Sem alguém com o perfil de vendedor ou balcão, a venda fica sem dono e não entra neste quadro.",
            // Vai para Usuários, não para Vendedores: /admin/vendedores só edita
            // meta e comissão de quem já existe (não há POST /admin/sellers), e
            // aquela tela manda para cá também. Botão para lá seria beco sem saída.
            action: <Button icon={Plus} onClick={() => navigate(link("usuarios"))}>Cadastrar em Usuários</Button>,
            testId: "report-sellers-empty",
          }}
        >
          {/* Mesmo motivo da curva ABC: empilhado cabe no celular sem esconder
              a comissão, que é a coluna que importa neste quadro. */}
          <ul className="divide-y divide-slate-100">
            {sellers.map((s) => (
              <li key={s.id} className="px-5 py-2.5 flex items-center gap-3" data-testid="report-seller-row">
                <div className="min-w-0 flex-1">
                  <p className="text-slate-900 truncate">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.sales_count} vendas · {brl(s.month_sold)}</p>
                </div>
                <span className="font-mono tabular-nums font-semibold text-brand-700 shrink-0">{brl(s.month_commission)}</span>
              </li>
            ))}
          </ul>
        </ReportPanel>
      </div>
    </div>
  );
}
